import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaArrowRight,
  FaBrain,
  FaMicrochip,
  FaCodeBranch,
  FaChevronLeft,
  FaChevronRight,
  FaRobot,
  FaBolt,
  FaShieldAlt
} from 'react-icons/fa';

import '../css/AIProjects.css';

import ramizHero from '../assets/ai/ramiz-hero.png';
import ramizFeatures from '../assets/ai/ramiz-features.png';
import ramizStack from '../assets/ai/ramiz-stack.png';
import ramizRoadmap from '../assets/ai/ramiz-roadmap.png';
import cloneAiShot from '../assets/ai/clone-ai.png';
import codeTesterShot from '../assets/ai/code-tester.png';

const featuredProject = {
  name: 'RamizKaraEski GPT',
  description:
    'A personal AI assistant platform with integrated workflows, scheduling logic, Gmail and calendar utilities, local LLM support, smart replies, and custom desktop automation.',
  link: 'https://www.ramizkaraeskigpt.xyz/',
  label: 'Featured AI System',
  tech: ['Local LLM', 'Smart Gmail', 'Calendar Control', 'System Utilities'],
  icon: <FaBrain />,
  slides: [
    {
      image: ramizHero,
      tag: 'Hero',
      title: 'Personal AI landing experience'
    },
    {
      image: ramizFeatures,
      tag: 'Features',
      title: 'Capabilities and live demo section'
    },
    {
      image: ramizStack,
      tag: 'Tech Stack',
      title: 'Stack and roadmap presentation'
    },
    {
      image: ramizRoadmap,
      tag: 'Roadmap',
      title: 'Product roadmap and release direction'
    }
  ]
};

const secondaryProjects = [
  {
    name: 'Voice Clone AI – Biometric Verification App',
    description:
      'Face verification system with selfie capture and voice clone workflows built with React and FastAPI.',
    imageUrl: cloneAiShot,
    link: 'https://www.oz-ai.xyz/',
    tech: ['Voice AI', 'FastAPI', 'React'],
    icon: <FaMicrochip />,
    accent: 'cyan'
  },
  {
    name: 'Code AI Tester',
    description:
      'AI-powered code analysis platform with ZIP upload, lint scans, auto docs, tests, and refactor workflows.',
    imageUrl: codeTesterShot,
    link: 'https://www.ozaicodetester.services/',
    tech: ['Code Analysis', 'LLM Workflows', 'Developer Tools'],
    icon: <FaCodeBranch />,
    accent: 'pink'
  }
];

const statItems = [
  {
    icon: <FaRobot />,
    title: 'Workflow AI',
    text: 'Multi-step assistant flows with practical actions'
  },
  {
    icon: <FaBolt />,
    title: 'Local Speed',
    text: 'Fast tool access and local inference oriented design'
  },
  {
    icon: <FaShieldAlt />,
    title: 'Utility Driven',
    text: 'Real integrations instead of static demo concepts'
  }
];

export default function AIProjects() {
  const [activeIndex, setActiveIndex] = useState(0);

  const slides = featuredProject.slides;
  const activeSlide = slides[activeIndex];

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <section id="ai-projects" className="ai-projects-section">
      <div className="ai-bg-orb ai-orb-1" />
      <div className="ai-bg-orb ai-orb-2" />
      <div className="ai-grid-overlay" />
      <div className="ai-watermark">INTELLIGENCE</div>

      <motion.div
        className="ai-heading-wrap"
        initial={{ opacity: 0, y: 35 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.25 }}
      >
        <span className="ai-kicker">Intelligent Systems</span>
        <h1>AI Projects</h1>
        <p>
          Applied AI products combining modern interfaces, backend systems,
          automation, and real-world productivity workflows.
        </p>
      </motion.div>

      <div className="ai-showcase-shell">
        <motion.a
          href={featuredProject.link}
          target="_blank"
          rel="noreferrer"
          className="ai-feature-card"
          initial={{ opacity: 0, y: 45, scale: 0.97 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8 }}
          whileHover={{
            y: -8,
            rotateX: 1.5,
            rotateY: -1.5,
            transition: { duration: 0.24 }
          }}
        >
          <div className="ai-feature-shine" />
          <div className="ai-feature-noise" />

          <div className="ai-feature-left">
            <div className="ai-feature-topbar">
              <span className="ai-feature-label">{featuredProject.label}</span>
              <span className="ai-feature-icon">{featuredProject.icon}</span>
            </div>

            <h2>{featuredProject.name}</h2>
            <p>{featuredProject.description}</p>

            <div className="ai-tech-row">
              {featuredProject.tech.map((item, index) => (
                <motion.span
                  key={item}
                  className="ai-tech-pill"
                  initial={{ opacity: 0, scale: 0.92 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + index * 0.06, duration: 0.3 }}
                  whileHover={{ y: -2 }}
                >
                  {item}
                </motion.span>
              ))}
            </div>

            <div className="ai-stat-grid">
              {statItems.map((item, index) => (
                <motion.div
                  key={item.title}
                  className="ai-stat-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.22 + index * 0.08, duration: 0.35 }}
                >
                  <div className="ai-stat-icon">{item.icon}</div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="ai-feature-cta">
              <span>Explore Project</span>
              <span className="ai-arrow">
                <FaArrowRight />
              </span>
            </div>
          </div>

          <div className="ai-feature-right">
            <div className="ai-preview-topbar">
              <div>
                <span className="ai-preview-tag">{activeSlide.tag}</span>
                <h3>{activeSlide.title}</h3>
              </div>

              <div className="ai-preview-controls">
                <button type="button" onClick={(e) => { e.preventDefault(); prevSlide(); }}>
                  <FaChevronLeft />
                </button>
                <button type="button" onClick={(e) => { e.preventDefault(); nextSlide(); }}>
                  <FaChevronRight />
                </button>
              </div>
            </div>

            <div className="ai-preview-stage">
              <motion.div
                className="ai-preview-back left"
                animate={{ y: [0, -10, 0], rotate: [-8, -10, -8] }}
                transition={{ repeat: Infinity, duration: 5.8, ease: 'easeInOut' }}
              >
                <img
                  src={slides[(activeIndex + 1) % slides.length].image}
                  alt="AI preview background left"
                />
              </motion.div>

              <motion.div
                className="ai-preview-back right"
                animate={{ y: [0, -12, 0], rotate: [8, 10, 8] }}
                transition={{ repeat: Infinity, duration: 6.1, ease: 'easeInOut' }}
              >
                <img
                  src={slides[(activeIndex + 2) % slides.length].image}
                  alt="AI preview background right"
                />
              </motion.div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  className="ai-preview-main"
                  initial={{ opacity: 0, y: 26, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -14, scale: 0.97 }}
                  transition={{ duration: 0.38 }}
                >
                  <img
                    src={activeSlide.image}
                    alt={activeSlide.title}
                    className="ai-feature-image"
                  />
                </motion.div>
              </AnimatePresence>

              <motion.div
                className="ai-floating-badge badge-1"
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 3.2, ease: 'easeInOut' }}
              >
                Smart Actions
              </motion.div>

              <motion.div
                className="ai-floating-badge badge-2"
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 3.8, ease: 'easeInOut' }}
              >
                Local Intelligence
              </motion.div>

              <motion.div
                className="ai-floating-badge badge-3"
                animate={{ y: [0, -7, 0] }}
                transition={{ repeat: Infinity, duration: 4.3, ease: 'easeInOut' }}
              >
                Utility Driven
              </motion.div>
            </div>

            <div className="ai-screen-thumbs">
              {slides.map((slide, index) => (
                <button
                  key={slide.tag}
                  type="button"
                  className={`ai-thumb ${activeIndex === index ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveIndex(index);
                  }}
                >
                  <img src={slide.image} alt={slide.tag} />
                </button>
              ))}
            </div>
          </div>
        </motion.a>

        <div className="ai-secondary-grid">
          {secondaryProjects.map((project, index) => (
            <motion.a
              href={project.link}
              target="_blank"
              rel="noreferrer"
              className={`ai-secondary-card accent-${project.accent}`}
              key={project.name}
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: index * 0.1, duration: 0.7 }}
              whileHover={{
                y: -8,
                rotateX: 1.4,
                rotateY: index % 2 === 0 ? -1.4 : 1.4,
                transition: { duration: 0.24 }
              }}
            >
              <div className="ai-secondary-shine" />

              <div className="ai-secondary-image-wrap">
                <img
                  src={project.imageUrl}
                  alt={project.name}
                  className="ai-secondary-image"
                />
              </div>

              <div className="ai-secondary-body">
                <div className="ai-secondary-icon">{project.icon}</div>
                <h3>{project.name}</h3>
                <p>{project.description}</p>

                <div className="ai-tech-row small">
                  {project.tech.map((item) => (
                    <span key={item} className="ai-tech-pill small">
                      {item}
                    </span>
                  ))}
                </div>

                <div className="ai-secondary-link">
                  <span>View Project</span>
                  <FaArrowRight />
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
